import React, { useMemo } from 'react'
import CopyToClipboard from 'react-copy-to-clipboard'
import timezoneList from 'constants/timezoneList'
import { TimeFormat, UpdateEventInput } from 'generated/graphql'
import { colors, _ } from 'utils'
import TextField from 'features/forms/components/TextField'
import DateField from 'features/forms/components/DateField'
import Editor from 'features/forms/components/Editor'
import Switch from 'features/forms/components/Switch'
import SelectField from 'features/forms/components/SelectField'
import env from 'env'
import constants from 'constants/constants'
import { useDispatch, useSelector } from 'react-redux'
import { allSpacesSelector } from 'features/events/eventSlice'
import Icon from 'components/Icon'
import styled from 'styled-components'
import { showNotification } from 'features/notifications'
import RadioButtonField from 'features/forms/components/RadioButtonField'

const S = {
    BackStageInfo: styled.div`
        display: flex;
        align-items: center;
        width: 100%;
        margin: -15px 5px 20px 5px;
        padding: 12px;
        background: ${colors.primaryOpacity(0.03)};
        border: 1px solid ${colors.iconOpacity(0.1)};
        border-radius: 5px;
    `,
    BackStageText: styled.span`
        font-size: 14px;
        color: ${colors.grayTitle};
    `,
    BackStageCopy: styled.div`
        display: flex;
        align-items: center;
        margin-left: auto;
        font-style: 14px;
        font-weight: 700;
        color: ${colors.primary};
        opacity: 0.75;
        cursor: pointer;
        & > span {
            transform: scaleY(1.2);
            margin-right: 6px;
            margin-bottom: 4px;
        }

        &:hover {
            opacity: 1;
        }
    `
}

type Props = {
    values: UpdateEventInput & {
        maxDurationInDays?: number
        openedDaysLeft?: number
        paid: boolean
        firstPublishedAt: string
        isActivationRequested: boolean
        organization: {
            subdomain: string
        }
    },
}

const SettingFormContent = ({ values }: Props) => {
    const isLite = constants.isLite
    const spaces = useSelector(allSpacesSelector)
    const dispatch = useDispatch()

    const remainingDates = useMemo(() => {
        if (!values?.firstPublishedAt || !values?.startDate || !values?.endDate) {
            return null
        }
        const total = values?.maxDurationInDays
        const remaining = values?.openedDaysLeft
        return <b>{_('generalsettings.form.remaining_days', { remaining, total })}</b>
    }, [
        values?.firstPublishedAt,
        values?.startDate,
        values?.endDate,
        values?.maxDurationInDays,
        values?.openedDaysLeft
    ])
    const eventUrl = isLite ? `${values?.organization?.subdomain}.evd.events/` : env.url

    const showCopiedMessage = () => {
        dispatch(showNotification({
            body: _('notification.copied'),
            type: 'success',
            displayDuration: 1500
        }))
    }

    return (
        <>
            <Switch
                name="published"
                label={(
                    <span>
                        {_('generalsettings.form.opened')}
                        &nbsp;
                        {remainingDates}
                    </span>
                )}
                isDisabled={!values?.paid}
                helperText={!values?.paid && _('generalsettings.form.opened_disable_not_paid')}
            />
            {
                !values.published && (
                    <S.BackStageInfo>
                        <Icon
                            icon="info"
                            size={18}
                            color={colors.primaryOpacity(0.75)}
                            className="mb-3px mr-2"
                        />
                        <S.BackStageText>
                            {_('generalsettings.back_stage_description')}
                        </S.BackStageText>
                        <CopyToClipboard text={`${eventUrl}${values.slug}/backstage`} onCopy={showCopiedMessage}>
                            <S.BackStageCopy>
                                <Icon icon="clipboard" size={13} color={colors.primary} />
                                COPY
                            </S.BackStageCopy>
                        </CopyToClipboard>
                    </S.BackStageInfo>
                )
            }
            <TextField
                name="name"
                isRequired
                label={_('generalsettings.form.event_name')}
                helperText={_('generalsettings.form.event_name_helper_text')}
                lg={1/2}
            />
            <TextField
                name="slug"
                isRequired
                label={_('generalsettings.form.event_code')}
                lg={1/2}
                helperText={_('generalsettings.form.event_code_helper_text')}
                prependText={eventUrl}
                isDisabled={isLite}
                textToCopy={`${eventUrl}${values.slug}`}
            />
            <DateField
                name="startDate"
                isRequired
                label={_('generalsettings.form.start_date')}
                helperText={_('generalsettings.form.start_date_helper_text')}
                lg={1/2}
                shouldCloseOnSelect={false}
                dateFormat="MMM do, yyyy"
            />
            <DateField
                name="endDate"
                isRequired
                label={_('generalsettings.form.end_date')}
                helperText={_('generalsettings.form.end_date_helper_text')}
                lg={1/2}
                shouldCloseOnSelect={false}
                dateFormat="MMM do, yyyy"
            />
            <SelectField
                name="virtual.homeSpaceId"
                label={_('generalsettings.form.home_space')}
                helperText={_('generalsettings.form.home_space_helper_text')}
                options={spaces || []}
                optionValue="id"
                optionLabel="name"
                lg={1/2}
            />
            <SelectField
                name="timezone"
                label={_('generalsettings.form.timezone')}
                options={timezoneList}
                lg={1/2}
                isClearable={false}
            />
            <SelectField
                name="timeFormat"
                label={_('generalsettings.form.time_format')}
                options={[
                    { label: _('time.format_24'), value: TimeFormat.Format24Hour },
                    { label: _('time.format_12'), value: TimeFormat.Format12Hour },
                ]}
                lg={1/2}
                isClearable={false}
            />
            <RadioButtonField
                name="virtual.startWithLoginTab"
                label={_('generalsettings.form.login_tab_selection')}
                options={[
                    { label: _('generalsettings.form.start_with_login_tab'), value: 'login' },
                    { label: _('generalsettings.form.start_with_request_code_tab'), value: 'request' },
                ]}
                helperText={_('generalsettings.form.login_tab_selection_help_text')}
            />
            <Editor
                name="description"
                label={_('show.form.description')}
                className="auto-size-content"
                enableRawView
            />
        </>
    )
}

export default SettingFormContent
